import type { ColumnFiltersState, RowData } from "@tanstack/react-table";
import { getBasicTableV3EnumFilterSelectedValues } from "@/components/table/v3/BasicTableV3/BasicTableV3.enumFilters";
import {
  BASIC_TABLE_V3_MATCHES_NO_ROWS,
  type BasicTableV3ActiveFilter,
  type BasicTableV3ColumnFilterConfig,
  type BasicTableV3FilterOperator,
  type BasicTableV3ResolvedFilterColumn,
  type BasicTableV3ServerFilterResult,
} from "@/components/table/v3/BasicTableV3/BasicTableV3.types";
import {
  isMultiValueFilterConfig,
  normalizeLegacyBasicTableV3FilterOperator,
} from "@/components/table/v3/BasicTableV3/BasicTableV3.utils";

/**
 * Shared value encoders and URL codecs for server-driven BasicTableV3 filters.
 * The server-table controller consumes these codecs so chip state can drive the
 * request instead of filtering only the rows already loaded in the browser.
 */

export interface BasicTableV3ServerFilterCodec {
  /** Separates the operator from the values in the URL param. Default "::". */
  querySeparator?: string;
  /** Separates individual values in the URL param. Default "||". */
  valueSeparator?: string;
}

const DEFAULT_QUERY_SEPARATOR = "::";
const DEFAULT_VALUE_SEPARATOR = "||";

/** Selected, non-blank values of a filter (empty when nothing is chosen). */
export const basicTableV3ServerFilterHasValue = (
  filter: Pick<BasicTableV3ActiveFilter, "value">,
): boolean => getBasicTableV3EnumFilterSelectedValues(filter.value).length > 0;

type BasicTableV3ServerValueFilter = Pick<BasicTableV3ActiveFilter, "matchesNothing" | "value">;

/**
 * Normalized, de-duplicated string values for an operator whose server meaning
 * is already fixed by the column (for example "is any of", "=", or
 * "contains"). Closed domains supporting "is not" must use
 * `basicTableV3FilterToServerValues` so the domain complement is honored.
 */
export const basicTableV3FilterToServerStrings = (
  filter: BasicTableV3ServerValueFilter | undefined,
): string[] | undefined => {
  if (filter?.matchesNothing === true) {
    return undefined;
  }
  const values = Array.from(
    new Set(
      (Array.isArray(filter?.value) ? filter.value : [filter?.value])
        .flatMap((value) => (typeof value === "string" ? [value.trim()] : []))
        .filter(Boolean),
    ),
  );
  return values.length > 0 ? values : undefined;
};

/** Singular form of `basicTableV3FilterToServerStrings`. Single-value filter
 * configs should prevent more than one value from reaching this boundary. */
export const basicTableV3FilterToServerString = (
  filter: BasicTableV3ServerValueFilter | undefined,
): string | undefined => basicTableV3FilterToServerStrings(filter)?.[0];

/** Map the normalized selected strings into a repeated backend value, dropping
 * unknown labels, de-duplicating mapped values, and omitting an empty result. */
export const basicTableV3FilterToMappedServerValues = <T>(
  filter: BasicTableV3ServerValueFilter | undefined,
  mapValue: (value: string) => T | null | undefined,
): T[] | undefined => {
  const mappedValues = (basicTableV3FilterToServerStrings(filter) ?? []).flatMap((value) => {
    const mapped = mapValue(value);
    return mapped == null ? [] : [mapped];
  });
  const values = Array.from(new Set(mappedValues));
  return values.length > 0 ? values : undefined;
};

/**
 * The labels a closed-domain chip lets through, honoring the "is not" operator
 * as the complement within `domain`. Returns [] when nothing is selected (= no
 * filter). Open domains (options drawn from loaded rows, "is any of" only) don't
 * need this — read `getBasicTableV3EnumFilterSelectedValues` directly, since
 * there is no fixed domain to complement against.
 */
export const basicTableV3AllowedLabels = (
  filter: Pick<BasicTableV3ActiveFilter, "operator" | "value">,
  domain: readonly string[],
): string[] => {
  const selected = getBasicTableV3EnumFilterSelectedValues(filter.value);
  if (selected.length === 0) {
    return [];
  }
  return filter.operator === "is not"
    ? domain.filter((label) => !selected.includes(label))
    : domain.filter((label) => selected.includes(label));
};

/**
 * A closed-domain chip → the flattened, de-duplicated backend values it allows.
 * Each allowed label maps (via `labelToValues`) to zero or more values — proto
 * enums, DB ids, whatever the request expects. A nullish or empty selection
 * yields `undefined`, the controller's canonical "omit this request field"
 * value. An active selection whose complement or mapping is empty yields
 * `BASIC_TABLE_V3_MATCHES_NO_ROWS` so it cannot broaden into an unfiltered
 * request.
 * This is the client-label → server-value crossing that the client-side enum
 * helpers (`facetFilterFn`) never perform.
 */
export const basicTableV3FilterToServerValues = <T>(
  filter: Pick<BasicTableV3ActiveFilter, "operator" | "value"> | undefined,
  domain: readonly string[],
  labelToValues: (label: string) => readonly T[],
): BasicTableV3ServerFilterResult<T[]> => {
  if (filter == null) {
    return undefined;
  }
  const selected = getBasicTableV3EnumFilterSelectedValues(filter.value);
  if (selected.length === 0) {
    return undefined;
  }
  const allowedLabels = basicTableV3AllowedLabels(filter, domain);
  if (allowedLabels.length === 0) {
    return BASIC_TABLE_V3_MATCHES_NO_ROWS;
  }
  const values: T[] = [];
  for (const label of allowedLabels) {
    for (const value of labelToValues(label)) {
      if (!values.includes(value)) {
        values.push(value);
      }
    }
  }
  return values.length > 0 ? values : BASIC_TABLE_V3_MATCHES_NO_ROWS;
};

/**
 * Singular sibling of `basicTableV3FilterToServerValues`, for a closed-domain
 * chip that maps to ONE scalar (or boolean) request value rather than a repeated
 * one. Zero effective labels means "match no rows"; all effective labels mean
 * "no filter"; exactly one maps to the scalar. Any other result is
 * unrepresentable and throws rather than silently omitting an active filter and
 * broadening the request. Configure scalar-backed chips as single-select unless
 * every offered operator preserves this invariant.
 * A boolean toggle is this helper over a two-value domain, e.g.
 * `basicTableV3FilterToServerValue(f, ["Has trace", "No trace"], (l) => l === "Has trace")`.
 */
export const basicTableV3FilterToServerValue = <T>(
  filter: Pick<BasicTableV3ActiveFilter, "operator" | "value"> | undefined,
  domain: readonly string[],
  labelToValue: (label: string) => T,
): BasicTableV3ServerFilterResult<T> => {
  if (filter == null) {
    return undefined;
  }
  const selected = getBasicTableV3EnumFilterSelectedValues(filter.value);
  if (selected.length === 0) {
    return undefined;
  }
  const allowed = basicTableV3AllowedLabels(filter, domain);
  if (allowed.length === 0) {
    return BASIC_TABLE_V3_MATCHES_NO_ROWS;
  }
  if (allowed.length === domain.length) {
    return undefined;
  }
  if (allowed.length === 1) {
    return labelToValue(allowed[0]);
  }
  throw new Error(
    `BasicTableV3 scalar server filter cannot represent ${allowed.length} effective values.`,
  );
};

export const serializeBasicTableV3ServerFilter = (
  filter: Pick<BasicTableV3ActiveFilter, "operator" | "value">,
  codec?: BasicTableV3ServerFilterCodec,
): string => {
  const querySep = codec?.querySeparator ?? DEFAULT_QUERY_SEPARATOR;
  const valueSep = codec?.valueSeparator ?? DEFAULT_VALUE_SEPARATOR;
  return `${filter.operator}${querySep}${getBasicTableV3EnumFilterSelectedValues(filter.value).join(
    valueSep,
  )}`;
};

interface BasicTableV3FilterQueryParamOptions {
  filter: Pick<BasicTableV3ActiveFilter, "operator" | "value">;
  href: string;
  queryParam: string;
  queryParamNamespace: string;
}

/**
 * Adds a namespaced BasicTableV3 filter to an href while preserving its other query params.
 * Table filters use the multi-query-state `namespace:param` key format.
 */
export const withBasicTableV3FilterQueryParam = ({
  filter,
  href,
  queryParam,
  queryParamNamespace,
}: BasicTableV3FilterQueryParamOptions): string => {
  const hashIndex = href.indexOf("#");
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex);
  const hrefWithoutHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const queryIndex = hrefWithoutHash.indexOf("?");
  const pathname = queryIndex === -1 ? hrefWithoutHash : hrefWithoutHash.slice(0, queryIndex);
  const searchParams = new URLSearchParams(
    queryIndex === -1 ? undefined : hrefWithoutHash.slice(queryIndex + 1),
  );

  searchParams.set(
    `${queryParamNamespace}:${queryParam}`,
    serializeBasicTableV3ServerFilter(filter),
  );

  return `${pathname}?${searchParams.toString()}${hash}`;
};

export const deserializeBasicTableV3ServerFilter = (
  serialized: string,
  filterConfig: Pick<
    BasicTableV3ColumnFilterConfig,
    "operatorOptions" | "defaultOperator" | "isMultiValue" | "type"
  >,
  codec?: BasicTableV3ServerFilterCodec,
): { operator: BasicTableV3FilterOperator; value: string | string[] } => {
  const querySep = codec?.querySeparator ?? DEFAULT_QUERY_SEPARATOR;
  const valueSep = codec?.valueSeparator ?? DEFAULT_VALUE_SEPARATOR;
  const separatorIndex = serialized.indexOf(querySep);
  const rawOperator = separatorIndex >= 0 ? serialized.slice(0, separatorIndex) : "";
  const maybeOperator = normalizeLegacyBasicTableV3FilterOperator(rawOperator);
  const operatorRecognized = filterConfig.operatorOptions.includes(
    maybeOperator as BasicTableV3FilterOperator,
  );
  // An unprefixed value (e.g. a legacy ?agent_id=... deep link) falls back to
  // the default operator with the whole string as the value.
  const rawValue =
    separatorIndex >= 0 ? serialized.slice(separatorIndex + querySep.length) : serialized;
  const values = (operatorRecognized ? rawValue : serialized)
    .split(valueSep)
    .filter((entry) => entry.trim() !== "");
  return {
    operator: operatorRecognized
      ? (maybeOperator as BasicTableV3FilterOperator)
      : filterConfig.defaultOperator,
    value: isMultiValueFilterConfig(filterConfig) ? values : values[0] ?? "",
  };
};

/** Active filters → tanstack ColumnFiltersState for URL persistence. */
export const basicTableV3FiltersToQueryState = <TColumnId extends string>(
  activeFilters: readonly BasicTableV3ActiveFilter<TColumnId>[],
  codec?: BasicTableV3ServerFilterCodec,
): ColumnFiltersState =>
  activeFilters.filter(basicTableV3ServerFilterHasValue).map((filter) => ({
    id: filter.columnId,
    value: [serializeBasicTableV3ServerFilter(filter, codec)],
  }));

/** URL ColumnFiltersState → active filters (drops unknown columns / empties). */
export const basicTableV3QueryStateToFilters = <TData extends RowData, TColumnId extends string>(
  queryColumnFilters: readonly { id: string; value: unknown }[],
  filterColumnById: Partial<Record<TColumnId, BasicTableV3ResolvedFilterColumn<TData, TColumnId>>>,
  createFilter: (
    column: Pick<BasicTableV3ResolvedFilterColumn<TData, TColumnId>, "id" | "filterConfig">,
    overrides?: Partial<Pick<BasicTableV3ActiveFilter<TColumnId>, "operator" | "value">>,
  ) => BasicTableV3ActiveFilter<TColumnId>,
  codec?: BasicTableV3ServerFilterCodec,
): BasicTableV3ActiveFilter<TColumnId>[] =>
  queryColumnFilters.flatMap((queryFilter) => {
    const filterColumn = filterColumnById[queryFilter.id as TColumnId];
    const filterValues = Array.isArray(queryFilter.value) ? queryFilter.value : [];
    const firstValue = filterValues[0];
    if (filterColumn == null || typeof firstValue !== "string" || firstValue.length === 0) {
      return [];
    }
    const { operator, value } = deserializeBasicTableV3ServerFilter(
      firstValue,
      filterColumn.filterConfig,
      codec,
    );
    if (!basicTableV3ServerFilterHasValue({ value })) {
      return [];
    }
    // The URL operator is authoritative: callers reconcile only external URL
    // changes (their own write echoes are skipped), so a live chip's operator is
    // never stale here, while seeded defaults (e.g. "is not" Deleted) would be.
    return [createFilter(filterColumn, { operator, value })];
  });
